import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Section from 'components/common/Section';
import SectionHeader from './SectionHeader';
import { useTranslation } from 'react-i18next';

const SCENARIOS = [
  {
    question: '¿Sirve para iPhone 13?',
    product: 'Auriculares Bluetooth NovaBeat H550',
    sources: ['SIMULATOR_SOURCE_LISTING', 'SIMULATOR_SOURCE_QA'],
    answer:
      'Hola! Sí, los auriculares NovaBeat H550 son compatibles con iPhone 13 gracias a su Bluetooth 5.3. Funcionan perfectamente con iOS. Saludos, Sofía de Gestodo.'
  },
  {
    question: '¿Incluye cargador de pared?',
    product: 'Auriculares Bluetooth NovaBeat H550',
    sources: ['SIMULATOR_SOURCE_LISTING', 'SIMULATOR_SOURCE_POLICY'],
    answer:
      'Hola! El producto incluye cable USB-C de carga, pero no incluye cargador de pared. Esta información está detallada en la descripción del artículo. Saludos, Sofía de Gestodo.'
  },
  {
    question: '¿Cuánto tarda el envío a Córdoba?',
    product: 'Mochila Deportiva TrailPro 35L',
    sources: ['SIMULATOR_SOURCE_POLICY', 'SIMULATOR_SOURCE_QA'],
    answer:
      'Hola! Los tiempos de entrega los indica Mercado Libre según tu destino al momento de la compra. Despachamos dentro de las 24 horas hábiles de acreditado el pago. Saludos, Sofía de Gestodo.'
  }
];

// Steps: 0=idle, 1=question, 2=classify, 3=sources, 4=generate, 5=verify, 6=answer, 7=done-pause
const STEP_DELAYS = [800, 1200, 1500, 1400, 1000, 800, 0, 1800];
const ANSWER_TYPING_DELAY = 22;
const ANSWER_FINISH_BUFFER = 1400;

const getStepDelay = (step, scenario) => {
  if (step === 6) {
    return scenario.answer.length * ANSWER_TYPING_DELAY + ANSWER_FINISH_BUFFER;
  }

  return STEP_DELAYS[step] || 1000;
};

const CheckIcon = ({ done, active }) => (
  <div
    style={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transition: 'all 0.35s ease',
      background: done
        ? 'linear-gradient(135deg, #00d563 0%, #00b44a 100%)'
        : active
        ? 'rgba(44,123,229,0.15)'
        : 'rgba(200,210,230,0.18)',
      border: done
        ? 'none'
        : active
        ? '2px solid rgba(44,123,229,0.7)'
        : '2px solid rgba(200,210,230,0.25)'
    }}
  >
    {done ? (
      <FontAwesomeIcon icon="check" style={{ color: '#fff', fontSize: 11 }} />
    ) : active ? (
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          border: '2px solid rgba(44,123,229,0.9)',
          borderTopColor: 'transparent',
          display: 'inline-block',
          animation: 'sim-spin 0.7s linear infinite'
        }}
      />
    ) : (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'rgba(200,210,230,0.3)'
        }}
      />
    )}
  </div>
);

const TypingDots = () => (
  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
    {[0, 1, 2].map(i => (
      <span
        key={i}
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: 'rgba(44,123,229,0.7)',
          display: 'inline-block',
          animation: `sim-bounce 1.1s ease-in-out ${i * 0.18}s infinite`
        }}
      />
    ))}
  </span>
);

const SourceBadge = ({ labelKey, active, done, t }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '3px 10px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 500,
      transition: 'all 0.4s ease',
      background: done
        ? 'rgba(0,213,99,0.15)'
        : active
        ? 'rgba(44,123,229,0.15)'
        : 'rgba(255,255,255,0.06)',
      border: done
        ? '1px solid rgba(0,213,99,0.4)'
        : active
        ? '1px solid rgba(44,123,229,0.4)'
        : '1px solid rgba(255,255,255,0.1)',
      color: done ? '#00d563' : active ? 'rgba(44,123,229,0.9)' : 'rgba(255,255,255,0.35)'
    }}
  >
    <FontAwesomeIcon
      icon={done ? 'check' : active ? 'search' : 'book'}
      style={{ fontSize: 9 }}
    />
    {t(`LANDING.${labelKey}`)}
  </span>
);

const AgentSimulator = () => {
  const { t } = useTranslation();
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [step, setStep] = useState(0);
  const [visibleAnswer, setVisibleAnswer] = useState('');
  const timerRef = useRef(null);
  const answerTimerRef = useRef(null);

  const scenario = SCENARIOS[scenarioIdx];

  // Pause when tab is hidden; reset cleanly when it comes back
  useEffect(() => {
    const handleVisibility = () => {
      clearTimeout(timerRef.current);
      clearTimeout(answerTimerRef.current);

      if (document.hidden) {
        return;
      }

      setVisibleAnswer('');
      setStep(0);
      setScenarioIdx(si => (si + 1) % SCENARIOS.length);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(answerTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Reset any partially typed answer whenever we move before the answer step.
  useEffect(() => {
    if (step < 6) {
      clearTimeout(answerTimerRef.current);
      setVisibleAnswer('');
    }
  }, [step]);

  // Type the answer once when we enter the answer step.
  useEffect(() => {
    if (step !== 6) {
      return;
    }

    let i = 0;
    const tick = () => {
      i++;
      setVisibleAnswer(scenario.answer.slice(0, i));
      if (i < scenario.answer.length) {
        answerTimerRef.current = setTimeout(tick, ANSWER_TYPING_DELAY);
      }
    };

    setVisibleAnswer('');
    answerTimerRef.current = setTimeout(tick, ANSWER_TYPING_DELAY);

    return () => clearTimeout(answerTimerRef.current);
  }, [step, scenario.answer]);

  // Step sequencer
  useEffect(() => {
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      if (step >= 7) {
        setVisibleAnswer('');
        setScenarioIdx(si => (si + 1) % SCENARIOS.length);
        setStep(0);
        return;
      }

      setStep(prev => prev + 1);
    }, getStepDelay(step, scenario));

    return () => clearTimeout(timerRef.current);
  }, [step, scenario]);

  // step 1 = question visible
  // step 2 = classify active
  // step 3 = sources active
  // step 4 = generate active
  // step 5 = verify active
  // step 6 = answer visible
  const processingSteps = [
    { labelKey: 'SIMULATOR_STEP_CLASSIFY', done: step > 2, active: step === 2 },
    { labelKey: 'SIMULATOR_STEP_SOURCES', done: step > 3, active: step === 3 },
    { labelKey: 'SIMULATOR_STEP_GENERATE', done: step > 4, active: step === 4 },
    { labelKey: 'SIMULATOR_STEP_VERIFY', done: step > 5, active: step === 5 }
  ];

  const questionVisible = step >= 1;
  const sourcesActive = step === 3;
  const sourcesComplete = step > 3;
  const answerVisible = step >= 6;
  const allDone = step >= 6;

  return (
    <Section className="overflow-hidden" style={{ background: 'linear-gradient(160deg, #0d1b3e 0%, #0e2248 55%, #081529 100%)' }}>
      <style>{`
        @keyframes sim-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes sim-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes sim-fade-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes sim-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(44,123,229,0.35); }
          70% { box-shadow: 0 0 0 12px rgba(44,123,229,0); }
          100% { box-shadow: 0 0 0 0 rgba(44,123,229,0); }
        }
        .sim-fade-up { animation: sim-fade-up 0.45s ease both; }
        .sim-pulse-icon { animation: sim-pulse-ring 1.8s ease-in-out infinite; }
      `}</style>

      <SectionHeader
        title={t('LANDING.SIMULATOR_TITLE')}
        subtitle={t('LANDING.SIMULATOR_SUBTITLE')}
        className="text-center"
      />

      <Row className="justify-content-center mt-6">
        <Col xs={12} md={10} lg={8} xl={7}>
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20,
              padding: '28px 28px',
              backdropFilter: 'blur(12px)',
              height: 540,
              overflow: 'hidden'
            }}
          >
            {/* Header bar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 24,
                paddingBottom: 16,
                borderBottom: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              <div
                className={allDone ? 'sim-pulse-icon' : ''}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2c7be5 0%, #1a4fad 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <FontAwesomeIcon icon="robot" style={{ color: '#fff', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}>
                  MAPA Agent
                </div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
                  {scenario.product}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#00d563',
                    display: 'inline-block',
                    boxShadow: '0 0 6px #00d563'
                  }}
                />
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>En línea</span>
              </div>
            </div>

            {/* Incoming question */}
            {questionVisible && (
              <div className="sim-fade-up" style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.4)',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em'
                  }}
                >
                  <FontAwesomeIcon icon="arrow-down" style={{ marginRight: 5 }} />
                  {t('LANDING.SIMULATOR_INCOMING')}
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '4px 16px 16px 16px',
                    padding: '10px 16px',
                    color: '#fff',
                    fontSize: 14,
                    maxWidth: '80%'
                  }}
                >
                  {scenario.question}
                </div>
              </div>
            )}

            {/* Processing steps */}
            {step >= 2 && (
              <div
                className="sim-fade-up"
                style={{
                  background: 'rgba(44,123,229,0.07)',
                  border: '1px solid rgba(44,123,229,0.2)',
                  borderRadius: 12,
                  padding: '14px 16px',
                  marginBottom: 20
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {processingSteps.map((s, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        opacity: s.done || s.active ? 1 : 0.35,
                        transition: 'opacity 0.4s ease'
                      }}
                    >
                      <CheckIcon done={s.done} active={s.active} />
                      <span
                        style={{
                          color: s.done
                            ? '#00d563'
                            : s.active
                            ? '#fff'
                            : 'rgba(255,255,255,0.45)',
                          fontSize: 13,
                          fontWeight: s.active ? 500 : 400,
                          transition: 'color 0.35s ease',
                          flex: 1
                        }}
                      >
                        {t(`LANDING.${s.labelKey}`)}
                      </span>
                      {s.active && <TypingDots />}
                    </div>
                  ))}
                </div>

                {/* Sources consulted */}
                {(sourcesActive || sourcesComplete) && (
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: '1px solid rgba(255,255,255,0.07)',
                      display: 'flex',
                      gap: 6,
                      flexWrap: 'wrap'
                    }}
                  >
                    {scenario.sources.map((src, i) => (
                      <SourceBadge
                        key={src}
                        labelKey={src}
                        active={sourcesActive && i === 0}
                        done={sourcesComplete || (sourcesActive && i > 0)}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Answer */}
            {answerVisible && (
              <div className="sim-fade-up">
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(0,213,99,0.7)',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em'
                  }}
                >
                  <FontAwesomeIcon icon="check-circle" style={{ marginRight: 5 }} />
                  {t('LANDING.SIMULATOR_STEP_READY')}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}
                >
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #2c7be5 0%, #1a4fad 100%)',
                      borderRadius: '16px 4px 16px 16px',
                      padding: '12px 16px',
                      color: '#fff',
                      fontSize: 13,
                      lineHeight: 1.55,
                      maxWidth: '85%',
                      boxShadow: '0 4px 20px rgba(44,123,229,0.35)'
                    }}
                  >
                    {visibleAnswer}
                    {visibleAnswer.length < scenario.answer.length && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: 2,
                          height: 13,
                          background: 'rgba(255,255,255,0.8)',
                          marginLeft: 2,
                          verticalAlign: 'middle',
                          animation: 'sim-bounce 0.8s ease infinite'
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Idle state: show pulsing indicator */}
            {step === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 0',
                  gap: 8,
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: 13
                }}
              >
                <TypingDots />
                <span style={{ marginLeft: 4 }}>Esperando pregunta...</span>
              </div>
            )}
          </div>

          {/* Scenario indicator dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 18 }}>
            {SCENARIOS.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === scenarioIdx ? 20 : 8,
                  height: 8,
                  borderRadius: 99,
                  background: i === scenarioIdx ? '#2c7be5' : 'rgba(255,255,255,0.2)',
                  transition: 'all 0.35s ease',
                  display: 'inline-block'
                }}
              />
            ))}
          </div>
        </Col>
      </Row>
    </Section>
  );
};

export default AgentSimulator;
